import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, BookOpen, BookMarked, Calendar, CheckSquare, TrendingUp, Clock, AlertCircle, BarChart3, Settings, UserPlus, Wallet, ClipboardList, Bell, MessageCircle, Briefcase, AlertTriangle, TrendingDown, ArrowRight, Bus, HandCoins } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import { getPublicDisplayName, normalizeRole } from "../../utils/authUtils";
import { useTenant } from "../../context/TenantContext";
import "../../styles/DashboardLayout.css";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

interface DashboardStats {
  totalStudents: number;
  totalTeachers: number;
  totalClasses: number;
  totalSubjects: number;
  upcomingEvents: number;
  pendingTasks: number;
}

interface UserCountsResponse {
  admins: number;
  teachers: number;
  students: number;
  total: number;
  tenantId?: string;
}

interface ClassCountsResponse {
  classes: number;
}

interface SubjectCountsResponse {
  subjects: number;
}

interface RecentPayment {
  id: number;
  amount: number;
  paidAt: string;
  studentName?: string;
  className?: string;
}

interface RecentMessage {
  id: number;
  content: string;
  createdAt: string;
  senderName?: string;
  subject?: string;
}

interface ActivityItem {
  createdAt: string;
  actionType: string;
  actorName: string;
  description?: string;
}

interface AdminOverview {
  termStatus: { term: string; year: number };
  financialOverview: {
    totalCollected: number;
    totalOutstanding: number;
    unpaidStudents: number;
    recentPayments: RecentPayment[];
  };
  attendanceSummary: {
    present: number;
    absent: number;
    total: number;
    rate: number;
  };
  uniformOrders: {
    pending: number;
    approved: number;
    completed: number;
  };
  messages: {
    unread: number;
    recent: RecentMessage[];
  };
  teacherWorkload: {
    totalTeachers: number;
    teachersAssigned: number;
    classesWithoutTeachers: number;
  };
  upcomingEvents: Array<{ id: string; name: string; date: string; responsibleTeacher?: string | null }>;
  alerts: {
    unpaidFees: number;
    missingAttendance: number;
    resultsNotUploaded: number;
    classesWithoutTeachers: number;
  };
  recentActivity: ActivityItem[];
  charts: {
    feesTrend: Array<{ month: string; value: number }>;
    enrollmentTrend: Array<{ month: string; value: number }>;
  };
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({ totalStudents: 0, totalTeachers: 0, totalClasses: 0, totalSubjects: 0, upcomingEvents: 3, pendingTasks: 5 });
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const { hasModule } = useTenant();

  const token = localStorage.getItem("token") || "";
  const rawUserName = localStorage.getItem("userName") || "Admin";
  const userTitle = localStorage.getItem("userTitle") || "";
  const role = normalizeRole(localStorage.getItem("role"));
  const displayRole = role || "admin";
  const userName = getPublicDisplayName(rawUserName, userTitle, displayRole);
  const formatCurrency = (value: number) => new Intl.NumberFormat("en-ZM", { style: "currency", currency: "ZMW", maximumFractionDigits: 0 }).format(value || 0);
  const formatDate = (value?: string) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString();
  };
  const canUse = (moduleKey?: string) => !moduleKey || hasModule(moduleKey);
  const quickActions = [
    { label: "Manage Learners", to: "/students", Icon: Users, color: "text-blue-600", hover: "hover:bg-blue-50 hover:border-blue-100", module: "students" },
    { label: "Manage Teachers", to: "/teachers", Icon: BookOpen, color: "text-teal-600", hover: "hover:bg-teal-50 hover:border-teal-100", module: "teachers" },
    { label: "Add User", to: "/add-user", Icon: UserPlus, color: "text-orange-600", hover: "hover:bg-orange-50 hover:border-orange-100" },
    { label: "Manage Classes", to: "/allclasses", Icon: Building2, color: "text-purple-600", hover: "hover:bg-purple-50 hover:border-purple-100", module: "classes" },
    { label: "Manage Subjects", to: "/subjects", Icon: BarChart3, color: "text-amber-600", hover: "hover:bg-amber-50 hover:border-amber-100", module: "classes" },
    { label: "Library", to: "/library", Icon: BookMarked, color: "text-indigo-600", hover: "hover:bg-indigo-50 hover:border-indigo-100", module: "library" },
    { label: "Attendance", to: "/attendance", Icon: CheckSquare, color: "text-green-600", hover: "hover:bg-green-50 hover:border-green-100", module: "attendance" },
    { label: "Record Fees", to: "/finance/payments", Icon: Wallet, color: "text-emerald-600", hover: "hover:bg-emerald-50 hover:border-emerald-100", module: "fees" },
    { label: "Upload Results", to: "/record-assessments", Icon: ClipboardList, color: "text-sky-600", hover: "hover:bg-sky-50 hover:border-sky-100", module: "results" },
    { label: "Send Announcement", to: "/headteacher/notices", Icon: Bell, color: "text-amber-600", hover: "hover:bg-amber-50 hover:border-amber-100", module: "notices" },
    { label: "Approve Orders", to: "/shop/uniform-orders", Icon: Briefcase, color: "text-rose-600", hover: "hover:bg-rose-50 hover:border-rose-100", module: "uniform_orders" },
    { label: "Messages", to: "/messages", Icon: MessageCircle, color: "text-indigo-600", hover: "hover:bg-indigo-50 hover:border-indigo-100", module: "messaging" },
    { label: "Transport", to: "/transport", Icon: Bus, color: "text-teal-600", hover: "hover:bg-teal-50 hover:border-teal-100", module: "transport" },
    { label: "Timetable", to: "/timetable", Icon: Calendar, color: "text-rose-600", hover: "hover:bg-rose-50 hover:border-rose-100", module: "timetable" },
    { label: "School Expenses", to: "/finance/expenses", Icon: HandCoins, color: "text-emerald-600", hover: "hover:bg-emerald-50 hover:border-emerald-100", module: "expenses" }
  ].filter((item) => canUse(item.module));

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
        const [userCountsRes, classCountsRes, subjectCountsRes, overviewRes] = await Promise.allSettled([
          axios.get<UserCountsResponse>(getApiUrl("stats/userCounts"), authHeaders),
          axios.get<ClassCountsResponse>(getApiUrl("stats/classCounts"), authHeaders),
          axios.get<SubjectCountsResponse>(getApiUrl("stats/subjectCounts"), authHeaders),
          axios.get<AdminOverview>(getApiUrl("stats/admin-overview"), authHeaders)
        ]);

        const userCounts = userCountsRes.status === "fulfilled" ? userCountsRes.value.data : null;
        const classCounts = classCountsRes.status === "fulfilled" ? classCountsRes.value.data : null;
        const subjectCounts = subjectCountsRes.status === "fulfilled" ? subjectCountsRes.value.data : null;
        const overviewData = overviewRes.status === "fulfilled" ? overviewRes.value.data : null;

        setStats({
          totalStudents: userCounts?.students || 0,
          totalTeachers: userCounts?.teachers || 0,
          totalClasses: classCounts?.classes || 0,
          totalSubjects: subjectCounts?.subjects || 0,
          upcomingEvents: overviewData?.upcomingEvents?.length || 0,
          pendingTasks: 5
        });
        setOverview(overviewData);

        const failedResponses = [userCountsRes, classCountsRes, subjectCountsRes, overviewRes].filter(
          (result) => result.status === "rejected"
        );

        if (failedResponses.length > 0) {
          failedResponses.forEach((result) => {
            if (result.status === "rejected") {
              console.error("Dashboard request failed:", result.reason);
            }
          });

          const firstError = failedResponses[0]!;
          const message =
            firstError.status === "rejected"
              ? firstError.reason?.response?.data?.message || "Failed to load some dashboard data"
              : "Failed to load some dashboard data";
          setError(message);
        } else {
          setError("");
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchStats();
    } else {
      setError("You are not signed in. Please log in again.");
      setLoading(false);
    }
  }, [token]);



  if (loading) return <div className="flex items-center justify-center min-vh-100 bg-white"><div className="text-center"><div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div><p className="text-slate-400 font-medium">Loading dashboard...</p></div></div>;

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/30">
        <section className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black text-slate-800 tracking-tight">
              Welcome back <span className="text-slate-700">{userName}</span>
            </h1>
            <p className="text-slate-400 mt-2 font-medium">Here's what's happening at your school today.</p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-medium text-sm hover:bg-slate-50 transition-colors border border-slate-200"><Settings size={18} /> Settings</button>
            <Link to="/attendance" className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-medium text-sm hover:bg-blue-700 transition-colors"><CheckSquare size={18} /> Take Attendance</Link>
          </div>
        </section>
        {error && <div className="bg-red-50 border border-red-100 p-4 rounded-xl flex items-center gap-3 mb-8"><AlertCircle className="text-red-500" size={20} /><p className="text-red-600 font-medium">{error}</p></div>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/50">
            <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center"><Users className="text-blue-600" size={24} /></div><span className="text-green-500 text-sm font-medium flex items-center gap-1"><TrendingUp size={14} /> +12%</span></div>
            <h3 className="text-3xl font-bold text-slate-800">{stats.totalStudents}</h3><p className="text-slate-400 font-medium text-sm mt-1">Total Learners</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/50">
            <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center"><BookOpen className="text-teal-600" size={24} /></div><span className="text-green-500 text-sm font-medium flex items-center gap-1"><TrendingUp size={14} /> +5%</span></div>
            <h3 className="text-3xl font-bold text-slate-800">{stats.totalTeachers}</h3><p className="text-slate-400 font-medium text-sm mt-1">Total Teachers</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/50">
            <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center"><Building2 className="text-purple-600" size={24} /></div></div>
            <h3 className="text-3xl font-bold text-slate-800">{stats.totalClasses}</h3><p className="text-slate-400 font-medium text-sm mt-1">Active Classes</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/50">
            <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center"><Calendar className="text-amber-600" size={24} /></div></div>
            <h3 className="text-3xl font-bold text-slate-800">{stats.upcomingEvents}</h3><p className="text-slate-400 font-medium text-sm mt-1">Upcoming Events</p>
          </div>
        </div>





        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
              <h2 className="text-xl font-bold text-slate-800 mb-6">Quick Actions</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {quickActions.map(({ label, to, Icon, color, hover }) => (
                  <Link key={label} to={to} className={`flex flex-col items-center p-6 bg-slate-50 rounded-xl border border-transparent transition-all ${hover}`}>
                    <Icon className={`${color} mb-3`} size={28} />
                    <span className="font-medium text-slate-700">{label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">System Alerts</h2>
            <div className="space-y-4">
              {canUse("fees") && <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl"><div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><AlertTriangle className="text-amber-600" size={20} /></div><div><p className="font-medium text-slate-800">Unpaid Fees</p><p className="text-sm text-slate-500">{overview?.alerts.unpaidFees || 0} learners with balances</p></div></div>}
              {canUse("attendance") && <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0"><Clock className="text-blue-600" size={20} /></div><div><p className="font-medium text-slate-800">Missing Attendance</p><p className="text-sm text-slate-500">{overview?.alerts.missingAttendance || 0} classes not marked today</p></div></div>}
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl"><div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center shrink-0"><TrendingDown className="text-rose-600" size={20} /></div><div><p className="font-medium text-slate-800">Results Not Uploaded</p><p className="text-sm text-slate-500">{overview?.alerts.resultsNotUploaded || 0} classes pending results</p></div></div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0"><Users className="text-purple-600" size={20} /></div><div><p className="font-medium text-slate-800">Classes Without Teachers</p><p className="text-sm text-slate-500">{overview?.alerts.classesWithoutTeachers || 0} classes unassigned</p></div></div>
            </div>
          </div>
        </div>

        {canUse("fees") && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Financial Overview</h2>
                <p className="text-sm text-slate-500 mt-1">Term {overview?.termStatus.term || "Current"} {overview?.termStatus.year || ""}</p>
              </div>
              <Link to="/finance/fees" className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold hover:text-blue-700">View fees <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <p className="text-sm text-emerald-700 font-medium">Collected</p>
                <p className="text-2xl font-bold text-emerald-900 mt-2">{formatCurrency(overview?.financialOverview.totalCollected || 0)}</p>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                <p className="text-sm text-amber-700 font-medium">Outstanding</p>
                <p className="text-2xl font-bold text-amber-900 mt-2">{formatCurrency(overview?.financialOverview.totalOutstanding || 0)}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-sm text-slate-600 font-medium">Unpaid Learners</p>
                <p className="text-2xl font-bold text-slate-800 mt-2">{overview?.financialOverview.unpaidStudents || 0}</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-600 mb-3">Recent Payments</h3>
              <div className="space-y-3">
                {(overview?.financialOverview.recentPayments || []).length === 0 && (
                  <div className="text-sm text-slate-500">No recent payments recorded.</div>
                )}
                {(overview?.financialOverview.recentPayments || []).map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between gap-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <div>
                      <p className="font-medium text-slate-800">{payment.studentName || "Learner"} <span className="text-slate-400 font-normal">{payment.className ? `· ${payment.className}` : ""}</span></p>
                      <p className="text-xs text-slate-500">{formatDate(payment.paidAt)}</p>
                    </div>
                    <p className="font-semibold text-emerald-700">{formatCurrency(payment.amount || 0)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {canUse("attendance") && <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Attendance Summary</h2>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-sm text-slate-500">Today's attendance rate</p>
                <p className="text-3xl font-bold text-slate-800 mt-2">{Math.round((overview?.attendanceSummary.rate || 0) * 100)}%</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                  <p className="text-sm text-emerald-600">Present</p>
                  <p className="text-xl font-bold text-emerald-800">{overview?.attendanceSummary.present || 0}</p>
                </div>
                <div className="p-4 rounded-xl bg-rose-50 border border-rose-100">
                  <p className="text-sm text-rose-600">Absent</p>
                  <p className="text-xl font-bold text-rose-700">{overview?.attendanceSummary.absent || 0}</p>
                </div>
              </div>
              <Link to="/attendance" className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold hover:text-blue-700">Open Attendance Register <ArrowRight size={16} /></Link>
            </div>
          </div>}
        </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          {canUse("uniform_orders") && <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Uniform Orders</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-sm text-slate-600">Pending</span>
                <span className="text-xl font-bold text-slate-800">{overview?.uniformOrders.pending || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-sm text-slate-600">Approved</span>
                <span className="text-xl font-bold text-slate-800">{overview?.uniformOrders.approved || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-sm text-slate-600">Completed</span>
                <span className="text-xl font-bold text-slate-800">{overview?.uniformOrders.completed || 0}</span>
              </div>
              <Link to="/shop/uniform-orders" className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold hover:text-blue-700">Review orders <ArrowRight size={16} /></Link>
            </div>
          </div>}
          {canUse("messaging") && <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Messages & Notifications</h2>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-100">
                <p className="text-sm text-indigo-600">Unread messages</p>
                <p className="text-2xl font-bold text-indigo-800 mt-2">{overview?.messages.unread || 0}</p>
              </div>
              <div className="space-y-3">
                {(overview?.messages.recent || []).length === 0 && (
                  <div className="text-sm text-slate-500">No recent messages.</div>
                )}
                {(overview?.messages.recent || []).slice(0, 3).map((msg) => (
                  <div key={msg.id} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-sm font-medium text-slate-800">{msg.senderName || "User"} <span className="text-slate-400 font-normal">{msg.subject ? `· ${msg.subject}` : ""}</span></p>
                    <p className="text-xs text-slate-500 mt-1">{msg.content}</p>
                  </div>
                ))}
              </div>
              <Link to="/messages" className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold hover:text-blue-700">Open messages <ArrowRight size={16} /></Link>
            </div>
          </div>}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Teacher Workload</h2>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-sm text-slate-500">Total teachers</p>
                <p className="text-2xl font-bold text-slate-800 mt-2">{overview?.teacherWorkload.totalTeachers || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <p className="text-sm text-emerald-600">Assigned to classes</p>
                <p className="text-2xl font-bold text-emerald-800 mt-2">{overview?.teacherWorkload.teachersAssigned || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-rose-50 border border-rose-100">
                <p className="text-sm text-rose-600">Classes without teachers</p>
                <p className="text-2xl font-bold text-rose-700 mt-2">{overview?.teacherWorkload.classesWithoutTeachers || 0}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Recent Activity</h2>
            <div className="space-y-4">
              {(overview?.recentActivity || []).length === 0 && (
                <div className="text-sm text-slate-500">No recent activity found.</div>
              )}
              {(overview?.recentActivity || []).map((activity, idx) => (
                <div key={`${activity.actionType}-${idx}`} className="p-4 rounded-xl bg-slate-50 border border-slate-100 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-slate-800">{activity.actionType}</p>
                    <p className="text-sm text-slate-500 mt-1">{activity.description || activity.actorName}</p>
                  </div>
                  <p className="text-xs text-slate-400 whitespace-nowrap">{formatDate(activity.createdAt)}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Upcoming Events</h2>
            <div className="space-y-4">
              {(overview?.upcomingEvents || []).length === 0 && (
                <div className="text-sm text-slate-500">No upcoming events in the next 7 days.</div>
              )}
              {(overview?.upcomingEvents || []).map((event) => (
                <div key={event.id} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="font-semibold text-slate-800">{event.name}</p>
                  <p className="text-sm text-slate-500 mt-1">{formatDate(event.date)}</p>
                  {event.responsibleTeacher && <p className="text-xs text-slate-400 mt-2">Lead: {event.responsibleTeacher}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {canUse("fees") && <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Fees Collection Trend</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={overview?.charts.feesTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#94A3B8" />
                  <YAxis stroke="#94A3B8" />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Line type="monotone" dataKey="value" stroke="#2563EB" strokeWidth={3} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Enrollment Growth</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview?.charts.enrollmentTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#94A3B8" />
                  <YAxis stroke="#94A3B8" />
                  <Tooltip formatter={(value: number) => `${value} learners`} />
                  <Bar dataKey="value" fill="#10B981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
export default AdminDashboard;
